import { AvatarDropdown, AvatarName, Footer } from "@/components";
import { currentUser as queryCurrentUser } from "@/services/ant-design-pro/api";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { SettingDrawer } from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history } from "@umijs/max";
import { ConfigProvider } from "antd";
import defaultSettings from "../config/defaultSettings";
import TabsView from "./components/TabsView";
import { errorConfig } from "./requestErrorConfig";

const isDev = process.env.NODE_ENV === "development" || process.env.CI;
const loginPath = "/user/login";

/**
 * 判断是否为白名单页面（不需要登录的页面）
 */
const isWhiteListPath = (pathname: string): boolean => {
  const normalizedPath = pathname.toLowerCase().replace(/\/+$/, "");
  return normalizedPath.startsWith("/user") || normalizedPath === "";
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return undefined;
      }
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (error: any) {
      if (isDev) {
        console.error("[getInitialState] Failed to fetch current user:", error);
      }
      return undefined;
    }
  };

  const { location } = history;
  const pathname = location.pathname;

  if (isDev) {
    console.log("[getInitialState] Current pathname:", pathname);
  }

  // 白名单页面：登录、注册等页面不调用 currentUser 接口
  if (isWhiteListPath(pathname)) {
    if (isDev) {
      console.log("[getInitialState] In whitelist pages, skip currentUser");
    }
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 检查是否有 token
  const token = localStorage.getItem("token");
  if (!token) {
    if (isDev) {
      console.log("[getInitialState] No token, skip currentUser");
    }
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 有 token 才调用 currentUser 接口
  if (isDev) {
    console.log("[getInitialState] Has token, fetching currentUser");
  }
  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      // <Question key="doc" />,
      // <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      const pathname = location.pathname;

      // 白名单页面不做处理
      if (isWhiteListPath(pathname)) {
        return;
      }

      // 检查是否有 token
      const token = localStorage.getItem("token");
      if (!token) {
        if (isDev) {
          console.log("[onPageChange] No token, redirecting to login");
        }
        history.replace(loginPath);
        return;
      }

      // 如果没有用户信息，重定向到登录页
      if (!initialState?.currentUser) {
        if (isDev) {
          console.log("[onPageChange] No currentUser, redirecting to login");
        }
        history.replace(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      const { location } = history;
      const pathname = location.pathname.toLowerCase().replace(/\/+$/, "");
      const isLoginPage = pathname.startsWith("/user");
      return (
        <ConfigProvider theme={{ token: { motion: true } }}>
          {isLoginPage ? children : <TabsView>{children}</TabsView>}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: "https://adminv1.zhiliaoshebao.com",
  ...errorConfig,
};
